import { useCallback, useState, useMemo, useRef, useEffect } from 'react';
import {
  ReactFlow,
  Background,
  Controls,
  ControlButton,
  MiniMap,
  type NodeTypes,
  type EdgeTypes,
  type Node,
  type Edge,
  useNodesState,
  useEdgesState,
  addEdge,
  type Connection,
  ReactFlowProvider,
  useReactFlow,
  type OnSelectionChangeParams,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { StepNode } from '@/lib/steps/step-node';
import { WorkflowEditorHeader } from './components/WorkflowEditorHeader';
import type { WorkflowDefinitionResponse } from '@erp/rpc';
import type { ZodType } from 'zod';
import { definitionToGraph } from 'src/lib/workflows/definitionToGraph';
import { graphToDefinition } from 'src/lib/workflows/graphToDefinition';
import { WorkflowEditorPanel } from './components/WorkflowEditorPanel';
import { DeletableEdge } from './components/DeletableEdge';
import {
  ConnectionDragProvider,
  useConnectionDrag,
  areSocketTypesCompatible,
} from './context/ConnectionDragContext';
import { useRegisterAddNodeHandler } from './context/WorkflowEditorContext';
import { RenderModeProvider, useRenderMode } from './context/RenderModeContext';
import { cn } from 'src/lib/utils';
import { v4 as uuidv4 } from 'uuid';
import type { ListStepTypesResponse } from '@erp/rpc';
import { IOType } from '@erp/shared-types/node-graph';
import {
  FLOW_IN_SOCKET_ID,
  FLOW_OUT_SOCKET_ID,
  FLOW_CONTROL_SOCKET_TYPE,
  SystemEventMap,
  inferSocketTypeFromZod,
  isFieldOptional,
  getZodTypeName,
  findFloatingNodes,
  findUnconnectedRequiredSockets,
  detectCycles,
  countTriggerNodes,
  type ValidationStep,
  type ValidationEdge,
} from '@erp/workflows';
import {
  ArrowsInLineVerticalIcon,
  ArrowsOutLineVerticalIcon,
} from '@phosphor-icons/react';
import { isValidConnection } from 'src/lib/workflows/graph-utils';
import type {
  StepNode as StepNodeType,
  StepNodeData,
} from './components/config-renderers';
import { useToast } from '@/context/ToastContext';
import { useListStepTypes } from 'src/api/rpc/hooks/workflows/useWorkflowDefinitions';

/**
 * Step types that have config-dependent output sockets.
 * When config changes for these steps, output edges may need to be removed.
 */
const STEPS_WITH_DYNAMIC_OUTPUTS = new Set(['event_trigger']);

/**
 * Convert React Flow nodes to validation step format
 */
function nodesToValidationSteps(nodes: Node[]): ValidationStep[] {
  return nodes.map((node) => {
    const data = node.data as StepNodeData & {
      category?: string;
      _stepTypeData?: ListStepTypesResponse['stepTypes'][0];
    };
    const stepTypeData = data._stepTypeData;
    // Use category from node data (set by definitionToGraph for existing nodes)
    // or from stepTypeData (set when dropping new nodes from library)
    const category = data.category ?? stepTypeData?.category ?? 'Utility';

    return {
      id: node.id,
      type: data.stepType ?? 'unknown',
      category,
      sockets: {
        inputs:
          stepTypeData?.inputSockets.map((s) => ({
            id: s.id,
            name: s.name,
            required: s.required ?? true,
          })) ?? [],
        outputs:
          stepTypeData?.outputSockets.map((s) => ({
            id: s.id,
            name: s.name,
          })) ?? [],
      },
    };
  });
}

/**
 * Convert React Flow edges to validation edge format
 */
function edgesToValidationEdges(edges: Edge[]): ValidationEdge[] {
  return edges.map((edge) => ({
    id: edge.id,
    sourceStepId: edge.source,
    sourceSocketId: edge.sourceHandle ?? '',
    targetStepId: edge.target,
    targetSocketId: edge.targetHandle ?? '',
  }));
}

type GeneratedSocket = {
  id: string;
  name: string;
  type: string;
  required: boolean;
  description: string;
};

function humanizeSocketName(key: string): string {
  return key
    .split(/(?=[A-Z])/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function generateSocketsFromSchema(
  schema: ZodType,
  socketKind: 'input' | 'output',
): GeneratedSocket[] {
  // Use Zod v4 traits system to get type name
  const typeName = getZodTypeName(schema);

  if (typeName === 'ZodNever') {
    return [];
  }

  if (typeName === 'ZodObject') {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const shape = (schema as any).shape;
    if (shape && typeof shape === 'object') {
      return Object.entries(shape).map(([key, fieldSchema]) => {
        const inferredType = inferSocketTypeFromZod(fieldSchema as ZodType);
        const required = !isFieldOptional(fieldSchema as ZodType);

        return {
          id: key,
          name: humanizeSocketName(key),
          type: inferredType,
          required,
          description: `${socketKind === 'input' ? 'Input' : 'Output'} field: ${key}`,
        };
      });
    }
  }

  const singleType = inferSocketTypeFromZod(schema);
  return [
    {
      id: 'value',
      name: 'Value',
      type: singleType,
      required: true,
      description: `${socketKind === 'input' ? 'Input' : 'Output'} value`,
    },
  ];
}

function toSocketRecord(
  sockets: GeneratedSocket[],
  ioType: IOType,
): Record<string, { id: string; ioType: IOType; socketType: string }> {
  const record: Record<
    string,
    { id: string; ioType: IOType; socketType: string }
  > = {};

  sockets.forEach((socket) => {
    record[socket.name] = {
      id: socket.id,
      ioType,
      socketType: socket.type,
    };
  });

  return record;
}

const nodeTypes: NodeTypes = {
  stepNode: StepNode,
};

function getSocketTypeFromNode(
  node: Node,
  handleId: string | null | undefined,
  direction: 'input' | 'output',
): string {
  if (!handleId) return 'any';

  if (handleId === FLOW_IN_SOCKET_ID || handleId === FLOW_OUT_SOCKET_ID) {
    return FLOW_CONTROL_SOCKET_TYPE;
  }

  const nodeData = node.data as {
    inputs?: Record<string, { id: string; socketType?: string }>;
    outputs?: Record<string, { id: string; socketType?: string }>;
  };

  const sockets = direction === 'input' ? nodeData.inputs : nodeData.outputs;
  if (!sockets) return 'any';

  const socket = Object.values(sockets).find((s) => s.id === handleId);
  return socket?.socketType || 'any';
}

const edgeTypes: EdgeTypes = {
  deletable: DeletableEdge,
};

export interface WorkflowMetadata {
  name: string;
  description?: string;
  tags?: string[];
}

interface WorkflowEditorProps {
  definition: WorkflowDefinitionResponse;
  onSave: (
    steps: WorkflowDefinitionResponse['steps'],
    edges: WorkflowDefinitionResponse['edges'],
    metadata?: WorkflowMetadata,
  ) => Promise<void>;
  onCancel: () => void;
  isSaving?: boolean;
  isCreateMode?: boolean;
  initiallyCollapsed?: boolean;
  /**
   * Whether the editor is currently animating nodes/edges into view.
   * When true, save is disabled and the editor shows animated reveal.
   */
  isAnimating?: boolean;
}

function randomNodeDelayMs(): number {
  return Math.floor(500 + Math.random() * 501);
}

function randomEdgeDelayMs(): number {
  return Math.floor(200 + Math.random() * 201);
}

function WorkflowEditorInner({
  definition,
  onSave,
  onCancel,
  isSaving = false,
  isCreateMode = false,
  initiallyCollapsed = false,
  isAnimating: externalIsAnimating = false,
}: WorkflowEditorProps & { isAnimating?: boolean }) {
  const reactFlowWrapper = useRef<HTMLDivElement>(null);
  const { screenToFlowPosition, fitView } = useReactFlow();
  const { startDrag, endDrag } = useConnectionDrag();
  const { showToast } = useToast();
  const { renderMode, toggleRenderMode } = useRenderMode();

  const fullGraph = useMemo(() => definitionToGraph(definition), [definition]);

  // Pre-compute node ID to array index map for efficient edge reveal lookups
  const nodeIdToIndex = useMemo(() => {
    const map = new Map<string, number>();
    fullGraph.nodes.forEach((node, idx) => {
      map.set(node.id, idx);
    });
    return map;
  }, [fullGraph.nodes]);

  const [revealedNodeIds, setRevealedNodeIds] = useState<Set<string>>(
    () => new Set(externalIsAnimating ? [] : fullGraph.nodes.map((n) => n.id)),
  );
  const [revealedEdgeIds, setRevealedEdgeIds] = useState<Set<string>>(
    () => new Set(externalIsAnimating ? [] : fullGraph.edges.map((e) => e.id)),
  );
  const [isAnimationComplete, setIsAnimationComplete] =
    useState(!externalIsAnimating);

  useEffect(() => {
    if (!externalIsAnimating) {
      setRevealedNodeIds(new Set(fullGraph.nodes.map((n) => n.id)));
      setRevealedEdgeIds(new Set(fullGraph.edges.map((e) => e.id)));
      setIsAnimationComplete(true);
      return;
    }

    setRevealedNodeIds(new Set());
    setRevealedEdgeIds(new Set());
    setIsAnimationComplete(false);

    const timeouts: Array<ReturnType<typeof setTimeout>> = [];

    if (fullGraph.nodes.length === 0) {
      setIsAnimationComplete(true);
      return;
    }

    let cumulativeDelayMs = 0;

    fullGraph.nodes.forEach((node, index) => {
      if (index > 0) {
        cumulativeDelayMs += randomNodeDelayMs();
      }

      const isLastNode = index === fullGraph.nodes.length - 1;

      const nodeTimeout = setTimeout(() => {
        setRevealedNodeIds((prevNodeIds) => {
          const nextNodeIds = new Set(prevNodeIds);
          nextNodeIds.add(node.id);
          return nextNodeIds;
        });

        const edgesToReveal = fullGraph.edges.filter((edge) => {
          const sourceIndex = nodeIdToIndex.get(edge.source);
          const targetIndex = nodeIdToIndex.get(edge.target);

          if (sourceIndex === undefined || targetIndex === undefined) {
            return false;
          }

          // Reveal edge when current node completes a connection to an earlier node:
          // - Current node is target, source was revealed earlier (incoming edge)
          // - Current node is source, target was revealed earlier (outgoing edge)
          return (
            (targetIndex === index && sourceIndex < index) ||
            (sourceIndex === index && targetIndex < index)
          );
        });

        if (edgesToReveal.length > 0) {
          const edgeTimeout = setTimeout(() => {
            setRevealedEdgeIds((prevEdgeIds) => {
              const nextEdgeIds = new Set(prevEdgeIds);
              for (const edge of edgesToReveal) {
                nextEdgeIds.add(edge.id);
              }
              return nextEdgeIds;
            });

            if (isLastNode) {
              setIsAnimationComplete(true);
            }
          }, randomEdgeDelayMs());

          timeouts.push(edgeTimeout);
        } else if (isLastNode) {
          setIsAnimationComplete(true);
        }
      }, cumulativeDelayMs);

      timeouts.push(nodeTimeout);
    });

    return () => {
      for (const timeout of timeouts) {
        clearTimeout(timeout);
      }
    };
  }, [externalIsAnimating, fullGraph, nodeIdToIndex]);

  useEffect(() => {
    if (!externalIsAnimating) return;
    if (revealedNodeIds.size === 0) return;

    fitView({ duration: 300, padding: 0.4, maxZoom: 0.75 });
  }, [externalIsAnimating, revealedNodeIds.size, fitView]);

  const initialNodes = useMemo(
    () => fullGraph.nodes.filter((node) => revealedNodeIds.has(node.id)),
    [fullGraph.nodes, revealedNodeIds],
  );

  const initialEdges = useMemo(
    () =>
      fullGraph.edges.filter(
        (edge) =>
          revealedNodeIds.has(edge.source) &&
          revealedNodeIds.has(edge.target) &&
          revealedEdgeIds.has(edge.id),
      ),
    [fullGraph.edges, revealedEdgeIds, revealedNodeIds],
  );

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

  useEffect(() => {
    if (externalIsAnimating) {
      setNodes(initialNodes);
      setEdges(initialEdges);
    }
  }, [externalIsAnimating, initialNodes, initialEdges, setNodes, setEdges]);

  // Track if there are unsaved changes
  const [isDirty, setIsDirty] = useState(isCreateMode); // Start dirty in create mode

  // Track selected node for config panel
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);

  // Metadata state for create/edit mode
  const [workflowName, setWorkflowName] = useState(definition.name || '');
  const [workflowDescription, setWorkflowDescription] = useState(
    definition.description || '',
  );
  const [workflowTags, setWorkflowTags] = useState<string[]>(
    definition.tags || [],
  );

  // Track if metadata has changed
  const metadataChanged = useMemo(() => {
    const originalName = definition.name || '';
    const originalDescription = definition.description || '';
    const originalTags = definition.tags || [];
    const tagsChanged =
      workflowTags.length !== originalTags.length ||
      workflowTags.some((tag, i) => tag !== originalTags[i]);
    return (
      workflowName !== originalName ||
      workflowDescription !== originalDescription ||
      tagsChanged
    );
  }, [definition, workflowName, workflowDescription, workflowTags]);

  // Combined dirty state (graph changes OR metadata changes)
  const isAnyDirty = isDirty || metadataChanged;

  // Get the selected node from nodes array
  const selectedNode = useMemo(() => {
    if (!selectedNodeId) return null;
    const found = nodes.find((n) => n.id === selectedNodeId);
    if (!found) return null;
    // Validate it has the expected shape for StepNodeData
    if (found.type === 'stepNode' && 'label' in (found.data ?? {})) {
      return found as StepNodeType;
    }
    return null;
  }, [nodes, selectedNodeId]);

  // Compute validation state for visual feedback
  const validationState = useMemo(() => {
    const validationSteps = nodesToValidationSteps(nodes as Node[]);
    const validationEdges = edgesToValidationEdges(edges as Edge[]);

    const floatingNodeIds = new Set(
      findFloatingNodes(validationSteps, validationEdges),
    );
    const unconnectedSockets = findUnconnectedRequiredSockets(
      validationSteps,
      validationEdges,
    );
    const triggerCount = countTriggerNodes(validationSteps);

    // Group unconnected sockets by step ID for efficient lookup
    const unconnectedSocketsByStep = new Map<string, Set<string>>();
    for (const socket of unconnectedSockets) {
      if (!unconnectedSocketsByStep.has(socket.stepId)) {
        unconnectedSocketsByStep.set(socket.stepId, new Set());
      }
      unconnectedSocketsByStep.get(socket.stepId)!.add(socket.socketId);
    }

    return {
      floatingNodeIds,
      unconnectedSocketsByStep,
      triggerCount,
      hasFloatingNodes: floatingNodeIds.size > 0,
      hasUnconnectedRequiredSockets: unconnectedSockets.length > 0,
    };
  }, [nodes, edges]);

  // Inject validation state into nodes for visual feedback
  const nodesWithValidation = useMemo(() => {
    return nodes.map((node) => ({
      ...node,
      data: {
        ...node.data,
        isFloating: validationState.floatingNodeIds.has(node.id),
        unconnectedRequiredSockets:
          validationState.unconnectedSocketsByStep.get(node.id) ?? undefined,
      },
    }));
  }, [nodes, validationState]);

  // Transform edges for simple mode - consolidate multiple edges between same node pair
  const displayEdges = useMemo(() => {
    if (renderMode !== 'simple') {
      return edges;
    }

    // Group edges by source-target pair and keep only one edge per pair
    const seenPairs = new Set<string>();
    const simplifiedEdges: typeof edges = [];

    for (const edge of edges) {
      const pairKey = `${edge.source}->${edge.target}`;
      if (!seenPairs.has(pairKey)) {
        seenPairs.add(pairKey);
        simplifiedEdges.push({
          ...edge,
          id: `simple-${pairKey}`,
          sourceHandle: 'output',
          targetHandle: 'input',
        });
      }
    }

    return simplifiedEdges;
  }, [edges, renderMode]);

  // Handle node changes (movement, selection, etc.)
  const handleNodesChange = useCallback(
    (changes: Parameters<typeof onNodesChange>[0]) => {
      onNodesChange(changes);
      // Mark as dirty if any position changes
      const hasPositionChange = changes.some(
        (change) => change.type === 'position' && change.dragging === false,
      );
      if (hasPositionChange) {
        setIsDirty(true);
      }
    },
    [onNodesChange],
  );

  // Handle edge changes (deletion, etc.)
  const handleEdgesChange = useCallback(
    (changes: Parameters<typeof onEdgesChange>[0]) => {
      onEdgesChange(changes);
      const hasRemoval = changes.some((change) => change.type === 'remove');
      if (hasRemoval) {
        setIsDirty(true);
      }
    },
    [onEdgesChange],
  );

  const handleConnect = useCallback(
    (connection: Connection) => {
      if (!connection.source || !connection.target) return;

      if (!isValidConnection(connection.source, connection.target, edges)) {
        return;
      }

      const sourceNode = nodes.find((n) => n.id === connection.source);
      const targetNode = nodes.find((n) => n.id === connection.target);

      if (!sourceNode || !targetNode) return;

      const sourceSocketType = getSocketTypeFromNode(
        sourceNode,
        connection.sourceHandle,
        'output',
      );
      const targetSocketType = getSocketTypeFromNode(
        targetNode,
        connection.targetHandle,
        'input',
      );

      if (!areSocketTypesCompatible(sourceSocketType, targetSocketType)) {
        return;
      }

      setEdges((eds) =>
        addEdge(
          {
            ...connection,
            id: uuidv4(),
            animated: true,
          },
          eds,
        ),
      );
      setIsDirty(true);
    },
    [nodes, edges, setEdges],
  );

  // Handle connection start (for drag context)
  const handleConnectStart = useCallback(
    (
      _: unknown,
      params: {
        nodeId: string | null;
        handleId: string | null;
        handleType: 'source' | 'target' | null;
      },
    ) => {
      if (params.nodeId && params.handleId && params.handleType) {
        // Check if this is a flow control socket (implicit, not in node data)
        if (
          params.handleId === FLOW_IN_SOCKET_ID ||
          params.handleId === FLOW_OUT_SOCKET_ID
        ) {
          startDrag({
            nodeId: params.nodeId,
            handleId: params.handleId,
            handleType: params.handleType,
            socketType: FLOW_CONTROL_SOCKET_TYPE,
          });
          return;
        }

        // Find the socket type from the node data for typed sockets
        const node = nodes.find((n) => n.id === params.nodeId);
        let socketType = 'any';

        if (node?.data) {
          const nodeData = node.data as {
            inputs?: Record<string, { id: string; socketType?: string }>;
            outputs?: Record<string, { id: string; socketType?: string }>;
          };
          // Search in inputs and outputs for the handle
          const allSockets = [
            ...Object.values(nodeData.inputs || {}),
            ...Object.values(nodeData.outputs || {}),
          ];
          const socket = allSockets.find((s) => s.id === params.handleId);
          if (socket?.socketType) {
            socketType = socket.socketType;
          }
        }

        startDrag({
          nodeId: params.nodeId,
          handleId: params.handleId,
          handleType: params.handleType,
          socketType,
        });
      }
    },
    [startDrag, nodes],
  );

  // Handle connection end (for drag context)
  const handleConnectEnd = useCallback(() => {
    endDrag();
  }, [endDrag]);

  // Handle deleting a node
  const handleDeleteNode = useCallback(
    (nodeId: string) => {
      setNodes((nds) => nds.filter((node) => node.id !== nodeId));
      // React Flow automatically removes connected edges before calling onNodesDelete
      setIsDirty(true);
    },
    [setNodes],
  );

  // Handle drag over for drop zone
  const handleDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  // Handle drop from node library
  const handleDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();

      const stepTypeData = event.dataTransfer.getData(
        'application/workflow-step-type',
      );
      if (!stepTypeData) return;

      const stepType: ListStepTypesResponse['stepTypes'][0] =
        JSON.parse(stepTypeData);

      // Get the position where the node was dropped
      const position = screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });

      // Create a new node from the step type
      const newNodeId = uuidv4();
      const inputs: Record<
        string,
        { id: string; ioType: IOType; socketType: string }
      > = {};
      const outputs: Record<
        string,
        { id: string; ioType: IOType; socketType: string }
      > = {};

      stepType.inputSockets.forEach((socket) => {
        inputs[socket.name] = {
          // Use original socket.id from registry - orchestrator validates against these
          id: socket.id,
          ioType: IOType.INPUT,
          socketType: socket.type,
        };
      });

      stepType.outputSockets.forEach((socket) => {
        outputs[socket.name] = {
          // Use original socket.id from registry - orchestrator validates against these
          id: socket.id,
          ioType: IOType.OUTPUT,
          socketType: socket.type,
        };
      });

      const newNode: Node = {
        id: newNodeId,
        type: 'stepNode',
        position,
        data: {
          label: stepType.name,
          inputs,
          outputs,
          stepType: stepType.type,
          // Store original step type data for conversion back
          _stepTypeData: stepType,
        },
      };

      setNodes((nds) => [...nds, newNode] as typeof nds);
      setIsDirty(true);
      fitView({ duration: 300, padding: 0.4, maxZoom: 0.75 });
    },
    [screenToFlowPosition, setNodes, fitView],
  );

  const { data: stepTypesData } = useListStepTypes();

  const handleAddNodeFromAgent = useCallback(
    (stepType: string, stepName?: string, options?: { silent?: boolean }) => {
      const silent = options?.silent ?? false;

      if (!stepTypesData?.stepTypes) {
        if (!silent) {
          showToast({
            title: 'Cannot add node',
            message: 'Step types are not loaded yet. Please try again.',
            color: 'error',
          });
        }
        return;
      }

      const stepTypeData = stepTypesData.stepTypes.find(
        (st) => st.type === stepType,
      );
      if (!stepTypeData) {
        if (!silent) {
          showToast({
            title: 'Step type not found',
            message: `Could not find step type "${stepType}".`,
            color: 'error',
          });
        }
        return;
      }

      const existingNodes = nodes;
      const rightmostNode = existingNodes.reduce<Node | null>(
        (rightmost, n) => {
          if (!rightmost || n.position.x > rightmost.position.x) {
            return n;
          }
          return rightmost;
        },
        null,
      );

      const position = {
        x: rightmostNode ? rightmostNode.position.x + 400 : 100,
        y: rightmostNode ? rightmostNode.position.y : 200,
      };

      const newNodeId = uuidv4();
      const inputs: Record<
        string,
        { id: string; ioType: IOType; socketType: string }
      > = {};
      const outputs: Record<
        string,
        { id: string; ioType: IOType; socketType: string }
      > = {};

      stepTypeData.inputSockets.forEach((socket) => {
        inputs[socket.name] = {
          id: socket.id,
          ioType: IOType.INPUT,
          socketType: socket.type,
        };
      });

      stepTypeData.outputSockets.forEach((socket) => {
        outputs[socket.name] = {
          id: socket.id,
          ioType: IOType.OUTPUT,
          socketType: socket.type,
        };
      });

      const newNode: Node = {
        id: newNodeId,
        type: 'stepNode',
        position,
        data: {
          label: stepName || stepTypeData.name,
          inputs,
          outputs,
          stepType: stepTypeData.type,
          _stepTypeData: stepTypeData,
        },
      };

      const newEdges: Edge[] = [];

      if (rightmostNode) {
        const rightmostNodeData = rightmostNode.data as StepNodeData;
        const rightmostOutputs = rightmostNodeData?.outputs ?? {};

        for (const inputSocket of stepTypeData.inputSockets) {
          const matchingOutput = Object.values(rightmostOutputs).find(
            (out) =>
              out.socketType &&
              areSocketTypesCompatible(out.socketType, inputSocket.type),
          );
          if (matchingOutput) {
            newEdges.push({
              id: uuidv4(),
              source: rightmostNode.id,
              sourceHandle: matchingOutput.id,
              target: newNodeId,
              targetHandle: inputSocket.id,
              animated: true,
            });
          }
        }
      }

      setNodes((nds) => [...nds, newNode] as typeof nds);
      if (newEdges.length > 0) {
        setEdges((eds) => [...eds, ...newEdges]);
      }
      setIsDirty(true);
      fitView({ duration: 300, padding: 0.4, maxZoom: 0.75 });

      if (!silent) {
        showToast({
          title: 'Node added',
          message: `Added "${stepName || stepTypeData.name}" to the workflow.`,
          color: 'success',
        });
      }
    },
    [stepTypesData, nodes, setNodes, setEdges, fitView, showToast],
  );

  useRegisterAddNodeHandler(handleAddNodeFromAgent);

  const handleSave = useCallback(async () => {
    // Validate name is required in create mode
    if (isCreateMode && !workflowName.trim()) {
      showToast({
        title: 'Name required',
        message: 'Please enter a name for the workflow.',
        color: 'error',
      });
      return;
    }

    // Convert to validation format for checks
    const validationSteps = nodesToValidationSteps(nodes as Node[]);
    const validationEdges = edgesToValidationEdges(edges as Edge[]);

    // Check for cycles (always blocked)
    const cycleResult = detectCycles(validationSteps, validationEdges);
    if (cycleResult.hasCycle) {
      showToast({
        title: 'Cannot save',
        message:
          'Workflow contains a cycle which would cause infinite execution.',
        color: 'error',
      });
      return;
    }

    // Check for floating nodes (always blocked)
    const floatingNodes = findFloatingNodes(validationSteps, validationEdges);
    if (floatingNodes.length > 0) {
      showToast({
        title: 'Cannot save',
        message: `${floatingNodes.length} node${floatingNodes.length > 1 ? 's are' : ' is'} not connected to the workflow.`,
        color: 'error',
      });
      return;
    }

    // Check for unconnected required sockets (blocked only for active workflows)
    if (definition.isActive) {
      const unconnectedSockets = findUnconnectedRequiredSockets(
        validationSteps,
        validationEdges,
      );
      if (unconnectedSockets.length > 0) {
        showToast({
          title: 'Cannot save active workflow',
          message: `${unconnectedSockets.length} required input${unconnectedSockets.length > 1 ? 's are' : ' is'} not connected. Deactivate the workflow to save with incomplete connections.`,
          color: 'error',
        });
        return;
      }
    }

    // All validations passed, proceed with save
    const { steps, edges: definitionEdges } = graphToDefinition(
      nodes as Node[],
      edges as Edge[],
      definition,
    );

    // Build metadata object
    const metadata: WorkflowMetadata = {
      name: workflowName.trim(),
      description: workflowDescription.trim() || undefined,
      tags: workflowTags.length > 0 ? workflowTags : undefined,
    };

    await onSave(steps, definitionEdges, metadata);
    setIsDirty(false);
  }, [
    nodes,
    edges,
    definition,
    onSave,
    showToast,
    isCreateMode,
    workflowName,
    workflowDescription,
    workflowTags,
  ]);

  // Handle reset
  const handleReset = useCallback(() => {
    const { nodes: initialNodes, edges: initialEdges } =
      definitionToGraph(definition);
    setNodes(initialNodes);
    setEdges(initialEdges);
    setIsDirty(false);
    setSelectedNodeId(null);
    // Reset metadata
    setWorkflowName(definition.name || '');
    setWorkflowDescription(definition.description || '');
    setWorkflowTags(definition.tags || []);
  }, [definition, setNodes, setEdges]);

  // Handle selection change
  // Note: Selection is intentionally not cleared when clicking empty space.
  // Users must explicitly close the config panel.
  const handleSelectionChange = useCallback(
    ({ nodes: selectedNodes }: OnSelectionChangeParams) => {
      if (selectedNodes.length === 1) {
        setSelectedNodeId(selectedNodes[0].id);
      }
    },
    [],
  );

  // Handle config panel close
  const handleConfigPanelClose = useCallback(() => {
    setSelectedNodeId(null);
    setNodes((nds) => nds.map((node) => ({ ...node, selected: false })));
  }, [setNodes]);

  // Handle config change from config panel
  const handleConfigChange = useCallback(
    (nodeId: string, newConfig: Record<string, unknown>) => {
      // Find the node to check if it has dynamic outputs
      const node = nodes.find((n) => n.id === nodeId);
      const nodeData = node?.data as StepNodeData | undefined;
      const stepType = nodeData?.stepType;
      const oldConfig = nodeData?.config ?? {};

      // Check if this step type has config-dependent outputs
      const hasDynamicOutputs =
        stepType && STEPS_WITH_DYNAMIC_OUTPUTS.has(stepType);

      // Check if the config that affects outputs has changed
      // For event_trigger, this is the eventType field
      let outputsAffected = false;
      if (hasDynamicOutputs && stepType === 'event_trigger') {
        outputsAffected = oldConfig.eventType !== newConfig.eventType;
      }

      // If outputs are affected, find and remove edges from this node's outputs
      if (outputsAffected && nodeData?.outputs) {
        const outputHandleIds = new Set(
          Object.values(nodeData.outputs).map((s) => s.id),
        );

        setEdges((eds) => {
          const edgesToRemove = eds.filter(
            (edge) =>
              edge.source === nodeId &&
              edge.sourceHandle &&
              outputHandleIds.has(edge.sourceHandle),
          );
          const edgesToKeep = eds.filter(
            (edge) =>
              !(
                edge.source === nodeId &&
                edge.sourceHandle &&
                outputHandleIds.has(edge.sourceHandle)
              ),
          );

          // Show toast inside the setter where we have accurate count
          if (edgesToRemove.length > 0) {
            // Use setTimeout to escape the render cycle
            setTimeout(() => {
              showToast({
                title: 'Connections removed',
                message: `${edgesToRemove.length} connection${edgesToRemove.length > 1 ? 's' : ''} ${edgesToRemove.length > 1 ? 'were' : 'was'} removed because the output schema changed.`,
                color: 'warning',
              });
            }, 0);
          }

          return edgesToKeep;
        });
      }

      const dynamicOutputSockets =
        stepType === 'event_trigger' &&
        outputsAffected &&
        typeof newConfig.eventType === 'string' &&
        Object.prototype.hasOwnProperty.call(
          SystemEventMap,
          newConfig.eventType,
        )
          ? generateSocketsFromSchema(
              SystemEventMap[
                newConfig.eventType as keyof typeof SystemEventMap
              ],
              'output',
            )
          : undefined;

      const dynamicOutputs = dynamicOutputSockets
        ? toSocketRecord(dynamicOutputSockets, IOType.OUTPUT)
        : undefined;

      // Update the node config (and outputs for dynamic steps)
      setNodes((nds) =>
        nds.map((n) => {
          if (n.id !== nodeId) return n;

          const existingData = n.data as StepNodeData & {
            _stepTypeData?: ListStepTypesResponse['stepTypes'][0];
          };

          const nextData = {
            ...(existingData as unknown as Record<string, unknown>),
            config: newConfig,
            ...(dynamicOutputs ? { outputs: dynamicOutputs } : {}),
            ...(dynamicOutputSockets && existingData._stepTypeData
              ? {
                  _stepTypeData: {
                    ...existingData._stepTypeData,
                    outputSockets: dynamicOutputSockets.map((socket) => ({
                      id: socket.id,
                      name: socket.name,
                      type: socket.type,
                      description: socket.description,
                    })),
                  },
                }
              : {}),
          };

          return {
            ...n,
            data: nextData as typeof n.data,
          };
        }),
      );

      setIsDirty(true);
    },
    [nodes, setNodes, setEdges, showToast],
  );

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <WorkflowEditorHeader
        workflowName={workflowName}
        workflowDescription={workflowDescription}
        workflowTags={workflowTags}
        onNameChange={setWorkflowName}
        onDescriptionChange={setWorkflowDescription}
        onTagsChange={setWorkflowTags}
        isCreateMode={isCreateMode}
        isSaving={isSaving || !isAnimationComplete}
        isDirty={isAnyDirty}
        hasTrigger={validationState.triggerCount > 0}
        onSave={handleSave}
        onReset={handleReset}
        onCancel={onCancel}
      />

      <div
        ref={reactFlowWrapper}
        className={cn(
          'flex-1 min-h-0 bg-slate-50 dark:bg-slate-900/50',
          'transition-colors duration-200',
        )}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        <ReactFlow
          nodes={nodesWithValidation}
          edges={displayEdges}
          onNodesChange={handleNodesChange}
          onEdgesChange={handleEdgesChange}
          onConnect={handleConnect}
          onConnectStart={handleConnectStart}
          onConnectEnd={handleConnectEnd}
          onSelectionChange={handleSelectionChange}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          fitView
          fitViewOptions={{ maxZoom: 0.75, padding: 0.4 }}
          attributionPosition="bottom-left"
          proOptions={{ hideAttribution: true }}
          minZoom={0.2}
          maxZoom={1}
          defaultEdgeOptions={{
            animated: true,
            type: 'deletable',
          }}
          deleteKeyCode={['Backspace', 'Delete']}
          edgesReconnectable
          elementsSelectable={true}
          onNodesDelete={(deletedNodes) => {
            deletedNodes.forEach((node) => handleDeleteNode(node.id));
            if (deletedNodes.some((n) => n.id === selectedNodeId)) {
              setSelectedNodeId(null);
            }
          }}
          onEdgesDelete={() => {
            setIsDirty(true);
          }}
        >
          <Background />
          <Controls>
            <ControlButton
              onClick={toggleRenderMode}
              title={
                renderMode === 'default'
                  ? 'Switch to simple view'
                  : 'Switch to detailed view'
              }
            >
              {renderMode === 'default' ? (
                <ArrowsInLineVerticalIcon className="h-3 w-3" />
              ) : (
                <ArrowsOutLineVerticalIcon className="h-3 w-3" />
              )}
            </ControlButton>
          </Controls>
          <MiniMap />
          <WorkflowEditorPanel
            selectedNode={selectedNode}
            onNodeDeselect={handleConfigPanelClose}
            onConfigChange={handleConfigChange}
            initiallyCollapsed={initiallyCollapsed}
          />
        </ReactFlow>
      </div>
    </div>
  );
}

export function WorkflowEditor(props: WorkflowEditorProps) {
  return (
    <ReactFlowProvider>
      <ConnectionDragProvider>
        <RenderModeProvider>
          <WorkflowEditorInner {...props} />
        </RenderModeProvider>
      </ConnectionDragProvider>
    </ReactFlowProvider>
  );
}
